import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArchiveCard from "@/components/feature/ArchiveCard";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { events, siteInfo } from "@/mocks/archive";
import { getCategoryName, getEraShort } from "@/utils/archive";

export default function ArchiveDetail() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((item) => item.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  const related = useMemo(() => {
    if (!event) return [];
    return events
      .filter((item) => item.id !== event.id && item.category === event.category)
      .slice(0, 3);
  }, [event]);

  const jsonLd = useMemo(() => {
    if (!event) return [];
    const url = `${SITE_ORIGIN}/archive/${event.id}`;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: event.title,
        description: event.summary,
        inLanguage: "zh-CN",
        image: event.cover,
        author: { "@type": "Organization", name: siteInfo.name },
        publisher: { "@type": "Organization", name: siteInfo.name },
        mainEntityOfPage: url,
        about: getCategoryName(event.category),
        contentLocation: event.location,
        datePublished: "2024-06-01",
        dateModified: "2026-03-01",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "档案库", item: `${SITE_ORIGIN}/archive` },
          { "@type": "ListItem", position: 3, name: event.title, item: url },
        ],
      },
    ];
  }, [event]);

  useSeo({
    title: event
      ? `${event.title} | ${event.yearLabel} · 事件时间线档案`
      : "档案未找到 | 暗线档案",
    description: event
      ? `${event.yearLabel}，${event.location}。${event.summary}`
      : "该档案不存在或已被移除。",
    keywords: event
      ? [...event.tags, event.title, "事件时间线"].join(",")
      : "暗线档案",
    path: event ? `/archive/${event.id}` : "/archive",
    ogType: "article",
    ogImage: event?.cover,
    jsonLd,
  });

  if (!event) {
    return (
      <SiteLayout>
        <section className="w-full px-4 md:px-8 pt-32 pb-24 text-center">
          <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            没有找到这条档案
          </h1>
          <p className="mt-3 text-sm text-foreground-500">
            链接可能已过期，或该条目尚未收录。你可以回到档案库重新查找。
          </p>
          <Link
            to="/archive"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm whitespace-nowrap cursor-pointer bg-primary-500 text-background-950 hover:bg-primary-400 transition-colors"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回档案库
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const eraShort = getEraShort(event.era);

  return (
    <SiteLayout>
      {/* 封面 */}
      <section className="relative h-[380px] md:h-[460px] w-full overflow-hidden">
        <img
          src={event.cover}
          alt={`${event.title} 事件档案封面`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/85 via-background-950/75 to-background-50"></div>
        <div className="relative z-10 w-full h-full flex flex-col justify-end px-4 md:px-8 pb-10 pt-28">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "档案库", to: "/archive" },
              { label: getCategoryName(event.category), to: `/archive?cat=${event.category}` },
              { label: event.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="px-3 py-1 rounded-full text-xs bg-primary-500 text-background-950">
              {event.yearLabel}
            </span>
            <span className="px-3 py-1 rounded-full text-xs bg-secondary-900/60 text-secondary-200">
              {getCategoryName(event.category)}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-map-pin-line" aria-hidden="true"></i>
              {event.location}
            </span>
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-3xl md:text-5xl font-black leading-tight text-foreground-950">
            {event.title}
          </h1>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          {/* 正文 */}
          <article className="lg:col-span-2">
            <p className="text-base md:text-lg leading-relaxed text-foreground-700 border-l-2 border-primary-500 pl-5">
              {event.lead}
            </p>

            <section id="background" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                事件背景
              </h2>
              <div className="mt-5 space-y-5">
                {event.body.map((paragraph) => (
                  <p key={paragraph.slice(0, 18)} className="text-sm md:text-base leading-loose text-foreground-600">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section id="timeline" className="mt-14 scroll-mt-24">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                关键节点时间轴
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                以下节点按时间先后排列，涵盖从事件起始到后续影响的主要阶段。
              </p>
              <ol className="mt-8 relative border-l-2 border-background-300 pl-6 md:pl-10 space-y-9">
                {event.timeline.map((node) => (
                  <li key={node.date} className="relative">
                    <span className="absolute -left-[31px] md:-left-[47px] top-1.5 w-3 h-3 rounded-full bg-accent-500 ring-4 ring-background-50"></span>
                    <span className="font-heading text-sm font-bold text-primary-400">
                      {node.date}
                    </span>
                    <h3 className="mt-1.5 font-heading text-base md:text-lg font-semibold text-foreground-900">
                      {node.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{node.desc}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                标签
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-background-100 border border-background-200 text-foreground-600"
                  >
                    <i className="ri-hashtag" aria-hidden="true"></i>
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          </article>

          {/* 侧栏 */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">档案信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">发生时间</dt>
                    <dd className="text-foreground-900">{event.yearLabel}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">所属纪元</dt>
                    <dd className="text-foreground-900">{eraShort}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">主题分类</dt>
                    <dd className="text-foreground-900">{getCategoryName(event.category)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">地点</dt>
                    <dd className="text-right text-foreground-900">{event.location}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">阅读时长</dt>
                    <dd className="text-foreground-900">约 {event.readTime}</dd>
                  </div>
                </dl>
              </div>

              <nav
                aria-label="本页目录"
                className="rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <h2 className="font-heading text-base font-bold text-foreground-950">本页目录</h2>
                <ul className="mt-4 space-y-3 text-sm">
                  <li>
                    <a href="#background" className="cursor-pointer text-foreground-600 hover:text-primary-400 transition-colors">
                      事件背景
                    </a>
                  </li>
                  <li>
                    <a href="#timeline" className="cursor-pointer text-foreground-600 hover:text-primary-400 transition-colors">
                      关键节点时间轴
                    </a>
                  </li>
                </ul>
              </nav>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  {getCategoryName(event.category)} · 相关档案
                </h2>
                <ul className="mt-4 space-y-4">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/archive/${item.id}`}
                        className="group block cursor-pointer"
                      >
                        <span className="text-xs text-primary-400">{item.yearLabel}</span>
                        <span className="mt-1 block text-sm text-foreground-800 group-hover:text-primary-400 transition-colors">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                  {related.length === 0 && (
                    <li className="text-sm text-foreground-500">该分类下暂无其他档案。</li>
                  )}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-8 py-14 md:py-16 border-t border-background-200 bg-background-100">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              继续阅读
            </h2>
            <Link
              to="/archive"
              className="inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
            >
              全部档案
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((item) => (
              <ArchiveCard key={item.id} event={item} />
            ))}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}